import { FileTrackerError } from './FileTrackerError';
import {
  FileTrackerStageEnum,
  FileTrackerListFileProps,
  FileTrackerListProps,
  isFileLoadingProps,
  FileUploadLayers,
} from '../file-tracker-types';
import { AiFillCheckCircle, AiOutlineHourglass } from 'react-icons/ai/index.js';
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Tooltip,
  Progress,
  Box,
  SkeletonText,
  GridItem,
  Grid,
  Text,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { Badge, SummaryCard, Card, Spin } from '@ssar-app/uikit';

interface RecentFilesProps {
  getRecentFiles?: (
    fileLimit?: number,
  ) => Promise<FileTrackerListProps | Error>;
  isFileTrackerLoading?: isFileLoadingProps;
  setIsFileTrackerLoading?: (val1: isFileLoadingProps) => void;
  subCode: string;
}

export const RecentFiles = (props: RecentFilesProps) => {
  const {
    getRecentFiles,
    isFileTrackerLoading,
    setIsFileTrackerLoading,
    subCode,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [recentFiles, setRecentFiles] = useState<
    (FileTrackerListFileProps | undefined)[]
  >([]);
  const [error, setError] = useState<Error | null>(null);

  const FetchRecentFiles = async () => {
    if (getRecentFiles) {
      setIsLoading(true);
      const filesList = await getRecentFiles(4);
      if (filesList instanceof Error) {
        setError(filesList);
      } else {
        setRecentFiles(
          filesList?.files
            ? filesList?.files.filter((file, index) => index < 4)
            : [],
        );
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsFileTrackerLoading &&
      isFileTrackerLoading &&
      setIsFileTrackerLoading({
        ...isFileTrackerLoading,
        ...{ recentFiles: isLoading },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getProgress = (stage: FileTrackerListFileProps) => {
    switch (stage.stage) {
      case FileTrackerStageEnum.MALWARE_SCAN_INPROGRESS:
        return { progress: ['green', 'orange', 'unset'] };
      case FileTrackerStageEnum.MALWARE_SCAN_COMPLETED:
        return { progress: ['green', 'green', 'unset'] };
      case FileTrackerStageEnum.MALWARE_SCAN_FAILED:
        return { progress: ['green', 'red', 'unset'] };
      case FileTrackerStageEnum.FILE_SAMPLING_IN_PROGRESS:
        return { progress: ['green', 'green', 'orange'] };
      case FileTrackerStageEnum.FILE_SAMPLING_COMPLETED:
      case FileTrackerStageEnum.FILE_IS_AVAILABLE:
        return { progress: ['green', 'green', 'green'] };
      case FileTrackerStageEnum.FILE_SAMPLING_FAILED:
        return { progress: ['green', 'green', 'red'] };
      default:
        return stage.layer === FileUploadLayers.AscendDataCuration
          ? { progress: ['green', 'orange', 'unset'] }
          : stage.layer === FileUploadLayers.FileService
          ? { progress: ['green', 'green', 'orange'] }
          : { progress: ['green', 'unset', 'unset'] };
    }
  };

  useEffect(() => {
    if (subCode !== '') {
      FetchRecentFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCode]);

  return (
    <Spin
      spinning={isLoading}
      containerProps={{
        position: 'relative',
        zIndex: 'inherit',
      }}
      spinWrapperProps={{ zIndex: 1 }}
      tip='Loading Recent Files'
    >
      <Card mt={3} w='full'>
        <Text fontSize='md' fontWeight='semibold' textTransform='uppercase'>
          RECENT FILES PROGRESS
        </Text>
        {!error ? (
          <Flex gap={3} mt={3} className='recent-files-container'>
            {recentFiles.length !== 0
              ? recentFiles.map((file, index) =>
                  file ? (
                    <SummaryCard
                      variant='outline'
                      key={index}
                      title={moment(
                        new Date(file?.stageUpdateTime || 0),
                      ).format('D MMM YYYY')}
                      statProps={{
                        display: 'grid',
                      }}
                      secondaryTitle={
                        <Tooltip
                          wordBreak='break-all'
                          placement='top-start'
                          label={file?.fileName}
                        >
                          {file?.fileName}
                        </Tooltip>
                      }
                      footer={
                        <>
                          <Box sx={{ display: 'inline-flex' }}>
                            <Badge badgeSize='xs'>{file?.layer}</Badge>
                            {file.layer != FileUploadLayers.FileService && (
                              <>
                                <Text
                                  sx={{
                                    color: 'orange.400',
                                    position: 'relative',
                                    top: 13,
                                    width: 10,
                                    borderTop: '2px solid',
                                  }}
                                />
                                <Box
                                  sx={{
                                    h: 6,
                                    w: 6,
                                    background: 'white',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    border: '2px',
                                    borderColor: 'orange.400',
                                    display: 'flex',
                                    top: '2px',
                                    position: 'relative',
                                  }}
                                >
                                  <Icon
                                    sx={{ color: 'orange', fontSize: 'md' }}
                                    as={AiOutlineHourglass}
                                  />
                                </Box>
                                <Text
                                  sx={{
                                    color: 'gray.500',
                                    position: 'relative',
                                    top: 13,
                                    width: 10,
                                    borderTop: '2px solid',
                                    borderStyle: 'dashed',
                                  }}
                                />
                                <Badge
                                  badgeProps={{ bg: 'gray.500' }}
                                  badgeSize='xs'
                                >
                                  {file.layer == FileUploadLayers.Sts
                                    ? FileUploadLayers.AscendDataCuration
                                    : FileUploadLayers.FileService}
                                </Badge>
                              </>
                            )}
                          </Box>
                          <Grid
                            templateColumns={'repeat(3, 1fr)'}
                            gap={1}
                            maxWidth='500px'
                          >
                            {getProgress(file).progress.map((color, index) => (
                              <GridItem key={index}>
                                <Progress
                                  value={color != 'unset' ? 100 : 0}
                                  colorScheme={color}
                                  size='sm'
                                  sx={{ mt: 8, mb: 2 }}
                                  borderLeftRadius={index == 0 ? 5 : 0}
                                  borderRightRadius={index == 2 ? 5 : 0}
                                />
                              </GridItem>
                            ))}
                          </Grid>
                          {file?.stage ===
                            FileTrackerStageEnum.FILE_IS_AVAILABLE && (
                            <Icon
                              as={AiFillCheckCircle}
                              mr='8px'
                              color='#52c41a'
                            />
                          )}
                          {file?.stage}
                        </>
                      }
                    />
                  ) : (
                    ''
                  ),
                )
              : isLoading
              ? Array.from({ length: 5 }).map((_val, index) => (
                  <Box key={index} padding='6' boxShadow='md' bg='white'>
                    <SkeletonText mt='4' noOfLines={1} w={'50%'} />
                    <SkeletonText mt='4' noOfLines={1} />
                    <SkeletonText noOfLines={2} mt={8} />
                  </Box>
                ))
              : 'No Recent Files'}
          </Flex>
        ) : (
          <FileTrackerError
            reloadFn={() => {
              setError(null);
              FetchRecentFiles();
            }}
            errorMsg='We encountered an unexpected error while getting the recent files. Please try again.'
          />
        )}
      </Card>
    </Spin>
  );
};
